import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiRefreshCw, FiActivity, FiCheckCircle, FiStar, FiXCircle,
  FiX, FiSearch, FiCheck, FiChevronDown,
  FiMaximize2, FiCopy, FiList,
} from "react-icons/fi";
import {
  RadarMetricChart,
  ExecutionsLineChart,
  FailureDonutChart,
  ScoreBarChart,
} from "../../src/components/EvaluationChart";
import EvaluationCss from "../../src/assets/css/Evaluation.css";
import EvaluationDashboardService from "../../src/services/api/evaluationDashboard";
import NoEvaluationImage from "../assets/img/no-evaluation.png";
import { useParams, useNavigate} from "react-router-dom";

import { formatDuration } from "../settings/shared/commonMethods";

const BUCKET_COLORS = ["#ef4444", "#f59e0b", "#fbbf24", "#34d399", "#22c55e"];
const RANGE_LABELS = {
  "0.0-0.2": "0–0.2",
  "0.2-0.4": "0.2–0.4",
  "0.4-0.6": "0.4–0.6",
  "0.6-0.8": "0.6–0.8",
  "0.8-1.0": "0.8–1.0",
};

const FULL_LIST_PAGE_SIZE = 500;

const safeNumber = (val, fallback = 0) =>
  typeof val === "number" && !Number.isNaN(val) ? val : fallback;
const safeArray = (val) => (Array.isArray(val) ? val : []);

const timeAgo = (isoDate) => {
  if (!isoDate) return "—";
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "—";
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}
const prettyPrint = (val) => {
  if (val == null) return "{}";
  if (typeof val === "string") {
    try {
      return JSON.stringify(JSON.parse(val), null, 2);
    } catch {
      return val;
    }
  }
  return JSON.stringify(val, null, 2);
};

const buildCardStats = (summary) => {
  if (!summary) {
    return { totalExecutions: "0", successRate: "0.0%", avgScore: "0.00 / 1.00", failedExecutions: "0" };
  }
  const total = safeNumber(summary.totalExecutions);
  const failed = safeNumber(summary.failedExecutions);
  const rate = safeNumber(summary.successRate);
  const avg = safeNumber(summary.averageEvaluationScore);
  return {
    totalExecutions: total.toLocaleString(),
    successRate: `${rate.toFixed(1)}%`,
    avgScore: `${avg.toFixed(2)} / 1.00`,
    failedExecutions: failed.toLocaleString(),
  };
};

const buildMetricsTable = (metricOverview) =>
  safeArray(metricOverview).map((m) => ({
    name: m?.metricName || "Unknown",
    score: safeNumber(m?.score),
  }));

const buildRadarData = (metricsTable) =>
  safeArray(metricsTable).map((m) => ({ axis: m.name, value: m.score }));

const buildScoreDistribution = (scoreDistribution) =>
  safeArray(scoreDistribution).map((d, i) => ({
    range: RANGE_LABELS[d?.range] || d?.range || "—",
    count: safeNumber(d?.count),
    color: BUCKET_COLORS[i % BUCKET_COLORS.length],
  }));

const buildExecutionTrend = (executionTrend) =>
  safeArray(executionTrend).map((d) => {
    const success = safeNumber(d?.successful);
    const failed = safeNumber(d?.failed);
    return {
      date: d?.date || "—",
      Total: success + failed,
      Success: success,
      Failed: failed,
    };
  });

const buildTopAgents = (topAgents) =>
  safeArray(topAgents)
    .map((a) => ({
      name: a?.agentId || "Unknown Agent",
      executions: safeNumber(a?.executions),
      rate: safeNumber(a?.successRate),
      score: safeNumber(a?.avgScore),
    }))
    .sort((a, b) => b.rate - a.rate);

const buildRecentExecutions = (list) =>
  safeArray(list).map((e) => ({
    id: e?.id || `${e?.executionId}::${e?.agentId}`,
    executionId: e?.executionId || "—",
    agent: e?.agentId || "Unknown Agent",
    status: e?.status === "Success" ? "Success" : "Failed",
    score: safeNumber(e?.score),
    time: timeAgo(e?.time),
  }));

const buildMetricSummary = (detail) =>
  safeArray(detail?.metrics).map((m) => ({
    label: m?.metricName || "Unknown Metric",
    val: safeNumber(m?.score),
    ok: m?.status === "success",
    reason: typeof m?.reason === "string" ? m.reason.trim() : "",
  }));

const metricRowColor = (score) => {
  const s = safeNumber(score);
  if (s >= 0.9) return "#22c55e";
  if (s >= 0.7) return "#f59e0b";
  return "#ef4444";
};

/* ══════════════════════════════════════════════════════
   MODAL — shared shell used by both "view all" modals
══════════════════════════════════════════════════════ */
const Modal = ({ title, onClose, searchValue, onSearchChange, searchPlaceholder, loading, children }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="ev-modal-overlay" onClick={onClose}>
      <div className="ev-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ev-modal-header">
          <h3 className="ev-modal-title">{title}</h3>
          <button className="ev-modal-close" onClick={onClose} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>
        {onSearchChange && (
          <div className="ev-modal-search">
            <FiSearch size={14} />
            <input
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
            />
          </div>
        )}
        <div className="ev-modal-body">
          {loading ? <div className="ev-modal-loading">Loading…</div> : children}
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   Small styled <select> used by the Execution Details filters
══════════════════════════════════════════════════════ */
const FilterSelect = ({ label, value, onChange, children, disabled }) => (
  <div className="ev-filter-select-wrap">
    <label className="ev-filter-select-label">{label}</label>
    <div className="ev-filter-select-control">
      <select
        className="ev-filter-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {children}
      </select>
      <FiChevronDown size={14} className="ev-filter-select-chevron" />
    </div>
  </div>
);

/* ══════════════════════════════════════════════════════
   Searchable dropdown — styled panel with a search box,
   used for the Execution / Agent filters in Execution Details
   options: [{ value, label, sublabel? }]
══════════════════════════════════════════════════════ */
const SearchableDropdown = ({ label, value, options, onChange, placeholder, searchPlaceholder, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setQuery(""); }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  const filtered = query.trim()
    ? options.filter((o) =>
        o.label.toLowerCase().includes(query.trim().toLowerCase()) ||
        (o.sublabel || "").toLowerCase().includes(query.trim().toLowerCase())
      )
    : options;

  return (
    <div className="ev-filter-select-wrap" ref={rootRef}>
      <label className="ev-filter-select-label">{label}</label>
      <div className="ev-searchable-dropdown">
        <button
          type="button"
          className="ev-searchable-trigger"
          onClick={() => !disabled && setOpen((prev) => !prev)}
          disabled={disabled}
        >
          <span className="ev-searchable-trigger-text">
            {selected ? selected.label : (placeholder || "Select…")}
          </span>
          <FiChevronDown size={14} className={`ev-filter-select-chevron ${open ? "open" : ""}`} />
        </button>

        {open && (
          <div className="ev-searchable-panel">
            <div className="ev-searchable-search">
              <FiSearch size={13} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder || "Search…"}
                autoFocus
              />
            </div>
            <div className="ev-searchable-list">
              {filtered.length === 0 ? (
                <div className="ev-searchable-empty">No matches</div>
              ) : (
                filtered.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    className={`ev-searchable-item ${o.value === value ? "selected" : ""}`}
                    onClick={() => {
                      onChange(o.value);
                      setOpen(false);
                      setQuery("");
                    }}
                  >
                    <span
                      className="ev-searchable-item-dot"
                      style={o.dotColor ? { background: o.dotColor } : undefined}
                    />
                    <span className="ev-searchable-item-text">
                      <span className="ev-searchable-item-label">{o.label}</span>
                      {o.sublabel && <span className="ev-searchable-item-sublabel">{o.sublabel}</span>}
                    </span>
                    {o.value === value && <FiCheck size={14} className="ev-searchable-item-check" />}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   Input / Output card — full width, sizes to its own content
══════════════════════════════════════════════════════ */
const IOCard = ({ label, text, isError, copied, onCopy, onExpand }) => (
  <div className="ev-io-card">
    <div className="ev-io-card-header">
      <h4>{label}</h4>
      <div className="ev-io-actions">
        <button className="ev-io-icon-btn" title="Expand" onClick={onExpand}>
          <FiMaximize2 size={14} />
        </button>
        <button
          className={`ev-io-icon-btn ${copied ? "copied" : ""}`}
          onClick={onCopy}
          title={`Copy ${label.toLowerCase()}`}
        >
          {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
        </button>
      </div>
    </div>
    <div className={`ev-io-box ${isError ? "ev-io-box-error" : ""}`}>{text}</div>
  </div>
);

/* ══════════════════════════════════════════════════════
   Metric row — click to expand and reveal the eval reason
══════════════════════════════════════════════════════ */
const MetricRow = ({ metric, isOpen, onToggle }) => {
  const hasReason = Boolean(metric.reason);
  return (
    <div className={`ev-metric-row ${isOpen ? "ev-metric-row--open" : ""}`}>
      <button
        type="button"
        className="ev-metric-row-header"
        onClick={hasReason ? onToggle : undefined}
        style={!hasReason ? { cursor: "default" } : undefined}
      >
        <span className={`ev-metric-cell-icon ${metric.ok ? "ok" : "fail"}`}>
          {metric.ok ? "✓" : "✗"}
        </span>
        <span className="ev-metric-cell-label">{metric.label}</span>
        <span className={`ev-metric-cell-val ${metric.ok ? "ok" : "fail"}`}>
          {metric.val.toFixed(2)}
        </span>
        {hasReason && (
          <FiChevronDown size={14} className={`ev-metric-row-chevron ${isOpen ? "open" : ""}`} />
        )}
      </button>
      {isOpen && hasReason && (
        <div
          className="ev-metric-row-reason"
          style={{ borderLeftColor: metric.ok ? "#22c55e" : "#ef4444" }}
        >
          {metric.reason}
        </div>
      )}
    </div>
  );
};

/* ══════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════ */
const Evaluation = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [summary, setSummary] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [executionsFallback, setExecutionsFallback] = useState([]);
  const [agentsFallback, setAgentsFallback] = useState([]);

  /* ── Full lists — power the inline filters AND the "view all" modals ── */
  const [allAgentsFull, setAllAgentsFull] = useState([]);
  const [allExecutionsFull, setAllExecutionsFull] = useState([]);

  const [selectedExecutionId, setSelectedExecutionId] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);

  /* ── Inline Execution Details filters ── */
  const [filterAgentId, setFilterAgentId] = useState(""); // "" = All agents

  /* ── "View all" modal state (pure browse/lookup, no filtering side-effects) ── */
  const [agentsModalOpen, setAgentsModalOpen] = useState(false);
  const [executionsModalOpen, setExecutionsModalOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [executionSearch, setExecutionSearch] = useState("");
  const [metricSummaryOpen, setMetricSummaryOpen] = useState(true);

  /* ── Which metric rows have their reason expanded (indices, reset per execution) ── */
  const [expandedMetrics, setExpandedMetrics] = useState(() => new Set());

  /* ── Input/Output overlay: null | "input" | "output" ── */
  const [overlayField, setOverlayField] = useState(null);

  /* ── Copy feedback state ── */
  const [copiedField, setCopiedField] = useState(null); // "input" | "output" | null

  /* ── Ref used to auto-scroll to Execution Details on selection ── */
  const execSectionRef = useRef(null);

  const loadDashboard = async () => {
    if (!projectId) {
      setError("No project selected.");
      return Promise.resolve();
    }
    setError(null);
    try {
      const [summaryRes, analyticsRes, executionsRes, agentsRes, allAgentsRes, allExecutionsRes] = await Promise.all([
        EvaluationDashboardService.getSummary(projectId),
        EvaluationDashboardService.getAnalytics(projectId),
        EvaluationDashboardService.getExecutionsList(projectId, 0, 5),
        EvaluationDashboardService.getTopAgents(projectId, 0, 5),
        EvaluationDashboardService.getTopAgents(projectId, 0, FULL_LIST_PAGE_SIZE),
        EvaluationDashboardService.getExecutionsList(projectId, 0, FULL_LIST_PAGE_SIZE),
      ]);
      
      setSummary(summaryRes);
      setAnalytics(analyticsRes);
      setExecutionsFallback(safeArray(executionsRes?.content));
      setAgentsFallback(safeArray(agentsRes?.content));
      setAllAgentsFull(buildTopAgents(safeArray(allAgentsRes?.content)));
      setAllExecutionsFull(buildRecentExecutions(safeArray(allExecutionsRes?.content)));

      const fullList = buildRecentExecutions(safeArray(allExecutionsRes?.content));
      const firstId = fullList[0]?.id || null;
      setSelectedExecutionId((prev) => prev || firstId);
    } catch (err) {
      console.error("Failed to load evaluation dashboard", err);
      setError("Failed to load evaluation dashboard.");
    }
  };

  useEffect(() => {
    setLoading(true);
    loadDashboard().finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    if (!selectedExecutionId) {
      setSelectedExecution(null);
      return;
    }
    const [execId, agentId] = selectedExecutionId.split("::");
    EvaluationDashboardService.getExecutionDetail(execId, agentId)
      .then((res) => setSelectedExecution(res))
      .catch((err) => {
      console.error("Failed to load execution detail", err);
      setSelectedExecution(null);
    });

}, [selectedExecutionId]);

  /* Reset expanded reasons whenever a different execution is selected. */
  useEffect(() => {
    setExpandedMetrics(new Set());
  }, [selectedExecutionId]);

  /* Keep the Agent filter in sync whenever an execution gets selected from
     anywhere else, so the two inline selects never fall out of sync. */
  useEffect(() => {
    if (!selectedExecution?.agentId) return;
    setFilterAgentId(selectedExecution.agentId);
  }, [selectedExecution]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    loadDashboard().finally(() => setIsRefreshing(false));
  };

  /* ── Select an execution + scroll down to Execution Details ── */
  const selectExecution = (executionId) => {
    setSelectedExecutionId(executionId);
    requestAnimationFrame(() => {
      execSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleAgentClick = (agentId) => {
    setAgentsModalOpen(false);
    setFilterAgentId(agentId);
    const firstForAgent = allExecutionsFull.find((e) => e.agent === agentId);
    if (firstForAgent) {
      selectExecution(firstForAgent.id);
    } else {
      execSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const toggleMetricExpanded = (index) => {
    setExpandedMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const filteredAllAgents = useMemo(
    () =>
      allAgentsFull.filter((a) =>
        a.name.toLowerCase().includes(agentSearch.trim().toLowerCase())
      ),
    [allAgentsFull, agentSearch]
  );

  const filteredAllExecutions = useMemo(() => {
    const q = executionSearch.trim().toLowerCase();
    if (!q) return allExecutionsFull;
    return allExecutionsFull.filter(
      (e) => e.id.toLowerCase().includes(q) || e.agent.toLowerCase().includes(q)
    );
  }, [allExecutionsFull, executionSearch]);

  const executionOptionsForFilter = useMemo(() => {
    const seen = new Map();
    for (const e of allExecutionsFull) {
      if (!seen.has(e.executionId)) {
        seen.set(e.executionId, e);
      }
    }
    return Array.from(seen.values());
  }, [allExecutionsFull]);

  const currentExecId = selectedExecutionId?.split("::")[0];
  const agentOptionsForFilter = useMemo(
    () => allAgentsFull.filter((a) =>
      allExecutionsFull.some((e) => e.executionId === currentExecId && e.agent === a.name)
    ),
    [allAgentsFull, allExecutionsFull, currentExecId]
  );

  /* ── Copy handler ── */
  const handleCopy = async (field, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField((prev) => (prev === field ? null : prev)), 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const cardStats = useMemo(() => buildCardStats(summary), [summary]);
  const metricsTable = useMemo(() => buildMetricsTable(analytics?.metricOverview), [analytics]);
  const radarData = useMemo(() => buildRadarData(metricsTable), [metricsTable]);
  const barData = useMemo(() => buildScoreDistribution(analytics?.scoreDistribution), [analytics]);
  const lineData = useMemo(() => buildExecutionTrend(analytics?.executionTrend), [analytics]);
  const agents = useMemo(
    () => buildTopAgents(analytics?.topAgents?.length ? analytics.topAgents : agentsFallback),
    [analytics, agentsFallback]
  );
  const executions = useMemo(
    () =>
      buildRecentExecutions(
        analytics?.recentExecutions?.length ? analytics.recentExecutions : executionsFallback
      ),
    [analytics, executionsFallback]
  );
  const metricSummary = useMemo(() => buildMetricSummary(selectedExecution), [selectedExecution]);
  const metricPassCount = useMemo(() => metricSummary.filter((m) => m.ok).length, [metricSummary]);
  const metricFailCount = metricSummary.length - metricPassCount;
  const metricPassPct = metricSummary.length
    ? (metricPassCount / metricSummary.length) * 100
    : 0;

  /* Execution Details must show the same trace-derived status as everywhere
     else in the UI. selectedExecution.status is the raw EvaluationDO boolean
     field (currently always false in seed data) — NOT reliable for display.
     allExecutionsFull already carries the correct "Success"/"Failed" string
     computed from workflow_execution_message.execStatus, so look it up from
     there instead of re-deriving it from the merged detail object. */
  const selectedExecutionStatus = useMemo(() => {
    const fromList = allExecutionsFull.find((e) => e.id === selectedExecutionId)?.status;
    if (fromList) return fromList; // "Success" | "Failed"
    return selectedExecution ? (selectedExecution.status ? "Success" : "Failed") : null;
  }, [allExecutionsFull, selectedExecutionId, selectedExecution]);

  const durationText = useMemo(
  () => formatDuration(selectedExecution?.durationMs),
  [selectedExecution]
);  

  const inputText = useMemo(
    () => prettyPrint(selectedExecution?.metricInput?.input),
    [selectedExecution]
  );
  const outputText = useMemo(
    () => prettyPrint(selectedExecution?.metricInput?.actualOutput),
    [selectedExecution]
  );

  useEffect(() => {
    if (!overlayField) return;
    const onKey = (e) => { if (e.key === "Escape") setOverlayField(null); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [overlayField]);

  const cards = [
    { title: "Total Executions", value: cardStats.totalExecutions, color: "#4f46e5", Icon: FiActivity },
    { title: "Success Rate", value: cardStats.successRate, color: "#22c55e", Icon: FiCheckCircle },
    { title: "Avg Evaluation Score", value: cardStats.avgScore, color: "#3b82f6", Icon: FiStar },
    { title: "Failed Executions", value: cardStats.failedExecutions, color: "#ef4444", Icon: FiXCircle },
  ];

  if (loading) {
    return (
      <div className="ev-page ev-loading-page">
        <div className="ev-loading-wrap">
          <p className="ev-loading-text">Loading evaluation dashboard…</p>
          <div className="ev-progress-track">
            <div className="ev-progress-fill" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ev-page">
        <p>{error}</p>
        <button className="ev-btn-refresh" onClick={handleRefresh}>Retry</button>
      </div>
    );
  }

  if (allExecutionsFull.length === 0) {
    return (
      <div className="ev-empty-page">
        <div className="ev-empty-state">
          <img src={NoEvaluationImage} alt="No evaluations found" className="ev-empty-image" />
          <h2 className="ev-empty-title">No evaluations found</h2>
          <p className="ev-empty-message">
            There are no evaluation executions recorded for this project yet.
          </p>
          <div className="ev-empty-actions">
            <button className="ev-btn-refresh" onClick={handleRefresh} disabled={isRefreshing}>
              <span className={`ev-refresh-icon ${isRefreshing ? "ev-spin" : ""}`}>
                <FiRefreshCw size={14} />
              </span>
              {isRefreshing ? "Retrying…" : "Retry"}
            </button>
            <button className="ev-btn-secondary" onClick={() => navigate(-1)}>Go back</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ev-page">

      {/* ── Header ── */}
      <div className="ev-header">
        <div>
          <h1 className="ev-title">Evaluation Dashboard</h1>
          <p className="ev-subtitle">Monitor and evaluate agent performance and quality in real-time</p>
        </div>
        <div className="ev-header-actions">
          <button className="ev-btn-refresh" onClick={handleRefresh} disabled={isRefreshing}>
            <span className={`ev-refresh-icon ${isRefreshing ? "ev-spin" : ""}`}>
              <FiRefreshCw size={14} />
            </span>
            Refresh
          </button>
          <button className="ev-btn-close" onClick={() => navigate(-1)} aria-label="Close">
            <FiX size={18} /> 
          </button>
        </div>
      </div>

      {/* ── Metric Cards ── */}
      <div className="ev-metric-grid">
        {cards.map((c, i) => (
          <div className="ev-metric-card" key={i}>
            <div className="ev-metric-icon" style={{ background: c.color + "18", color: c.color }}>
              <c.Icon size={20} />
            </div>
            <div className="ev-metric-body">
              <p className="ev-metric-label">{c.title}</p>
              <h2 className="ev-metric-value">{c.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* ── Chart Row ── */}
      <div className="ev-chart-grid">

        <div className="ev-card ev-card-large">
          <h3 className="ev-card-title">Metric Overview</h3>
          <div className="ev-metric-overview">
            <RadarMetricChart
              data={radarData}
              overallScore={(cardStats.avgScore || "0.00").split(" ")[0]}
            />
            <div className="ev-metrics-table-wrap">
              <table className="ev-inner-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Score</th>
                  </tr>
                </thead>
                <tbody>
                  {metricsTable.map((m, i) => (
                    <tr key={i}>
                      <td>
                        <span className="ev-dot" style={{ background: metricRowColor(m.score) }} />
                        {m.name}
                      </td>
                      <td>{m.score.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="ev-card">
          <h3 className="ev-card-title">Executions Over Time</h3>
          <ExecutionsLineChart data={lineData} />
        </div>
      </div>

      {/* ── Table Row ── */}
      <div className="ev-table-grid">

        <div className="ev-card">
          <h3 className="ev-card-title">Top Agents by Success Rate</h3>
          <table className="ev-table">
            <thead>
              <tr>
                <th>Agent ID</th>
                <th>Executions</th>
                <th>Success Rate</th>
                <th>Avg Score</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a, i) => (
                <tr key={i} onClick={() => handleAgentClick(a.name)} style={{ cursor: "pointer" }}>
                  <td>{a.name}</td>
                  <td>{a.executions.toLocaleString()}</td>
                  <td>
                    <div className="ev-rate-wrap">
                      <div className="ev-rate-bar-bg">
                        <div className="ev-rate-bar" style={{ width: `${a.rate}%` }} />
                      </div>
                      <span>{a.rate}%</span>
                    </div>
                  </td>
                  <td>{a.score.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="ev-view-all" onClick={() => { setAgentSearch(""); setAgentsModalOpen(true); }}>
            View all agents →
          </button>
        </div>

        <div className="ev-card">
          <div className="ev-card-header-row">
            <h3 className="ev-card-title">Recent Executions</h3>
            <button
              className="ev-view-all-link"
              onClick={() => { setExecutionSearch(""); setExecutionsModalOpen(true); }}
            >
              View all →
            </button>
          </div>
          <table className="ev-table">
            <thead>
              <tr>
                <th>Execution ID</th>
                <th>Agent ID</th>
                <th>Status</th>
                <th>Score</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {executions.map((e, i) => (
                <tr key={i} onClick={() => selectExecution(e.id)} style={{ cursor: "pointer" }}>
                  <td className="ev-exec-id">{e.executionId}</td>
                  <td>{e.agent}</td>
                  <td>
                    <span className={`ev-badge ${e.status === "Success" ? "ev-badge-success" : "ev-badge-failed"}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className={e.status === "Success" ? "ev-score-good" : "ev-score-bad"}>
                    {e.score.toFixed(2)}
                  </td>
                  <td className="ev-time">{e.time}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ev-card">
          <h3 className="ev-card-title">Score Distribution</h3>
          <ScoreBarChart data={barData} />
        </div>
      </div>

      {/* ── Execution Details ── */}
      <div className="ev-card ev-exec-section" ref={execSectionRef}>
        <div className="ev-exec-header">
          <div className="ev-exec-header-left">
            <h3 className="ev-card-title" style={{ margin: 0 }}>Execution Details</h3>
            <span className="ev-exec-id-tag">ID: {selectedExecution?.agentExecutionId || "—"}</span>
            <span className={`ev-badge ${selectedExecutionStatus === "Success" ? "ev-badge-success" : "ev-badge-failed"}`}>
              {selectedExecutionStatus || "No data"}
            </span>
          </div>
          <div className="ev-exec-meta">
            <span>Agent: <strong>{selectedExecution?.agentId || "—"}</strong></span>
            <span className="ev-meta-sep" />
            <span>Duration: <strong>{durationText}</strong></span>
            <span className="ev-meta-sep" />
            <span>Time: <strong>{selectedExecution?.createdDate || "—"}</strong></span>
          </div>
        </div>

        {/* ── Inline Agent / Execution filters ── */}
        <div className="ev-filter-row">
          <SearchableDropdown
            label="Execution"
            value={currentExecId || ""}
            onChange={(val) => {
              const firstForExec = allExecutionsFull.find((e) => e.executionId === val);
              if (firstForExec) selectExecution(firstForExec.id);
            }}
            disabled={executionOptionsForFilter.length === 0}
            placeholder="No executions"
            searchPlaceholder="Search execution ID…"
            options={executionOptionsForFilter.map((e) => ({
              value: e.executionId,
              label: e.executionId,
              sublabel: `Score ${e.score.toFixed(2)}`,
              dotColor: metricRowColor(e.score),
            }))}
          />

          <SearchableDropdown
            label="Agent"
            value={filterAgentId}
            onChange={(val) => {
              const targetRow = allExecutionsFull.find(
                (e) => e.agent === val && e.executionId === currentExecId
              );
              if (targetRow) setSelectedExecutionId(targetRow.id);
            }}
            disabled={agentOptionsForFilter.length === 0}
            placeholder="No agents"
            searchPlaceholder="Search agent ID…"
            options={agentOptionsForFilter.map((a) => ({ value: a.name, label: a.name }))}
          />

          <span className="ev-filter-count">
            {agentOptionsForFilter.length} agent{agentOptionsForFilter.length === 1 ? "" : "s"} for {currentExecId}
          </span>
        </div>

        {/* ── Input / Output / Metric Summary — 3 columns, aligned ── */}
        <div className="ev-exec-3col-grid">
          <IOCard
            label="Input"
            text={inputText}
            copied={copiedField === "input"}
            onCopy={() => handleCopy("input", inputText)}
            onExpand={() => setOverlayField("input")}
          />
          <IOCard
            label="Actual output"
            text={outputText}
            isError={selectedExecutionStatus === "Failed"}
            copied={copiedField === "output"}
            onCopy={() => handleCopy("output", outputText)}
            onExpand={() => setOverlayField("output")}
          />

          <div className="ev-metric-summary-card">
            <div
              className="ev-metric-summary-header"
              onClick={() => setMetricSummaryOpen((prev) => !prev)}
            >
              <div className="ev-metric-summary-title-group">
                <h4 className="ev-metric-summary-title">Metric summary</h4>
                <span className="ev-metric-summary-count">
                  {metricPassCount} / {metricSummary.length} passed
                </span>
              </div>
              <span className={`ev-metric-summary-chevron ${metricSummaryOpen ? "" : "collapsed"}`}>
                <FiChevronDown size={16} />
              </span>
            </div>

            <div className="ev-metric-progress-bar">
              <div className="ev-metric-progress-fill" style={{ width: `${metricPassPct}%` }} />
            </div>

            {metricSummaryOpen && (
              <>
                {/* ── Passed / Failed / Total stat row ── */}
                <div className="ev-metric-stats-row">
                  <div className="ev-metric-stat-box passed">
                    <div className="ev-metric-stat-top">
                      <span className="ev-metric-stat-num">{metricPassCount}</span>
                      <FiCheckCircle size={18} className="ev-metric-stat-icon" />
                    </div>
                    <span className="ev-metric-stat-label">Passed</span>
                  </div>
                  <div className="ev-metric-stat-box failed">
                    <div className="ev-metric-stat-top">
                      <span className="ev-metric-stat-num">{metricFailCount}</span>
                      <FiXCircle size={18} className="ev-metric-stat-icon" />
                    </div>
                    <span className="ev-metric-stat-label">Failed</span>
                  </div>
                  <div className="ev-metric-stat-box total">
                    <div className="ev-metric-stat-top">
                      <span className="ev-metric-stat-num">{metricSummary.length}</span>
                      <FiList size={18} className="ev-metric-stat-icon" />
                    </div>
                    <span className="ev-metric-stat-label">Total</span>
                  </div>
                </div>

                {/* ── Expandable metric rows with reason ── */}
                <div className="ev-metric-list">
                  {metricSummary.map((m, i) => (
                    <MetricRow
                      key={i}
                      metric={m}
                      isOpen={expandedMetrics.has(i)}
                      onToggle={() => toggleMetricExpanded(i)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── View all agents modal (pure lookup — no filter side-effect) ── */}
      {agentsModalOpen && (
        <Modal
          title="All Agents"
          onClose={() => setAgentsModalOpen(false)}
          searchValue={agentSearch}
          onSearchChange={setAgentSearch}
          searchPlaceholder="Search agent ID…"
        >
          {filteredAllAgents.length === 0 ? (
            <div className="ev-modal-empty">No agents found.</div>
          ) : (
            <table className="ev-table">
              <thead>
                <tr>
                  <th>Agent ID</th>
                  <th>Executions</th>
                  <th>Success Rate</th>
                  <th>Avg Score</th>
                </tr>
              </thead>
              <tbody>
                {filteredAllAgents.map((a, i) => (
                  <tr key={i} onClick={() => handleAgentClick(a.name)} style={{ cursor: "pointer" }}>
                    <td>{a.name}</td>
                    <td>{a.executions.toLocaleString()}</td>
                    <td>
                      <div className="ev-rate-wrap">
                        <div className="ev-rate-bar-bg">
                          <div className="ev-rate-bar" style={{ width: `${a.rate}%` }} />
                        </div>
                        <span>{a.rate}%</span>
                      </div>
                    </td>
                    <td>{a.score.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {/* ── View all executions modal (pure lookup — no filter side-effect) ── */}
      {executionsModalOpen && (
        <Modal
          title="All Executions"
          onClose={() => setExecutionsModalOpen(false)}
          searchValue={executionSearch}
          onSearchChange={setExecutionSearch}
          searchPlaceholder="Search execution or agent ID…"
        >
          {filteredAllExecutions.length === 0 ? (
            <div className="ev-modal-empty">No executions found.</div>
          ) : (
            <table className="ev-table">
              <thead>
                <tr>
                  <th>Execution ID</th>
                  <th>Agent ID</th>
                  <th>Status</th>
                  <th>Score</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {filteredAllExecutions.map((e, i) => (
                  <tr
                    key={i}
                    onClick={() => {
                      setExecutionsModalOpen(false);
                      selectExecution(e.id);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="ev-exec-id">{e.executionId}</td>
                    <td>{e.agent}</td>
                    <td>
                      <span className={`ev-badge ${e.status === "Success" ? "ev-badge-success" : "ev-badge-failed"}`}>
                        {e.status}
                      </span>
                    </td>
                    <td className={e.status === "Success" ? "ev-score-good" : "ev-score-bad"}>
                      {e.score.toFixed(2)}
                    </td>
                    <td className="ev-time">{e.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal>
      )}

      {/* ── Input / Output expand overlay ── */}
      {overlayField && (
        <div className="ev-modal-overlay" onClick={() => setOverlayField(null)}>
          <div className="ev-io-overlay-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ev-modal-header">
              <h3 className="ev-modal-title">{overlayField === "input" ? "Input" : "Actual Output"}</h3>
              <button className="ev-modal-close" onClick={() => setOverlayField(null)} aria-label="Close">
                <FiX size={18} />
              </button>
            </div>
            <div className="ev-io-overlay-body ev-io-overlay-body-single">
              <div className="ev-io-overlay-section">
                <div className="ev-io-card-header">
                  <button
                    className={`ev-io-icon-btn ${copiedField === overlayField ? "copied" : ""}`}
                    onClick={() =>
                      handleCopy(overlayField, overlayField === "input" ? inputText : outputText)
                    }
                    title={`Copy ${overlayField}`}
                    style={{ marginLeft: "auto" }}
                  >
                    {copiedField === overlayField ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                </div>
                <div className="ev-io-box ev-io-box-large">
                  {overlayField === "input" ? inputText : outputText}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Evaluation;